/**
 * Registry traffic-light triage. Pure helpers so School Row / Action Required
 * stay aligned, and so later workflow states are not overridden by stale
 * SEF / refinery / missing schema_v2 job signals.
 */

export const QA_PHASE_POST_APPEAL = 'post_appeal';

const EMPTY_REFINERY = {
  evidence: { step: 0, label: '' },
  survey: { step: 0, label: '' },
};

/** Cycle states that mean evidence analysis is no longer the current action. */
export function isWorkflowPastEvidenceAnalysis(workflowStep) {
  const step = String(workflowStep || '').toLowerCase();
  return (
    step === 'report_compiling' ||
    step === 'ai_draft_ready' ||
    step === 'peer_review' ||
    step === 'internal_review_rejected' ||
    step === 'fact_check' ||
    step === 'appeals' ||
    step === 'accepted' ||
    step === 'finalised'
  );
}

function isPostAppealQa(reportMeta, school) {
  return (
    String(reportMeta?.qaPhase || '') === QA_PHASE_POST_APPEAL ||
    String(school?.qaPhase || '') === QA_PHASE_POST_APPEAL
  );
}

/**
 * @param {object} school
 * @param {{ status?: string } | null} audit
 * @param {{ meta?: { refinery?: { evidence?: { step?: number } }, qaPhase?: string } } | null} report
 */
export function getAuditTurn(school, audit, report) {
  const auditStatus = audit?.status || school?.auditStatus || 'Draft';
  const workflowStep = school?.workflowStep || '';

  if (workflowStep === 'finalised') {
    return { color: 'green', label: 'Status: Complete', priority: 3 };
  }

  const refineryStep = report?.meta?.refinery?.evidence?.step || school?.refineryStep || 0;

  if (school?.status === 'inactive') {
    return { color: 'red', label: 'Action: Activate Access', priority: 1 };
  }

  // Incomplete analysis must not override a later real workflow state
  // (released to school, peer review, authoring, etc.).
  if (
    !isWorkflowPastEvidenceAnalysis(workflowStep) &&
    auditStatus === 'Submitted' &&
    (!report || refineryStep < 3)
  ) {
    return { color: 'red', label: 'Action: AI evidence analysis', priority: 1 };
  }

  if (workflowStep === 'report_compiling' || workflowStep === 'ai_draft_ready') {
    return { color: 'red', label: 'Action: Write Report', priority: 1 };
  }
  if (workflowStep === 'peer_review') {
    const postAppeal = isPostAppealQa(report?.meta, school);
    return {
      color: 'red',
      label: postAppeal ? 'Post-appeal QA decision' : 'Submitted for Internal Review',
      priority: 1,
    };
  }
  if (workflowStep === 'internal_review_rejected') {
    return { color: 'red', label: 'Revisions Required (Peer Review)', priority: 1 };
  }
  if (workflowStep === 'appeals') {
    return { color: 'red', label: 'Amend draft & send to QA', priority: 1 };
  }
  if (workflowStep === 'accepted') {
    return { color: 'red', label: 'Action: Finalise Report', priority: 1 };
  }

  if (workflowStep === 'fact_check') {
    return { color: 'amber', label: 'Status: Sent to School', priority: 2 };
  }
  if (auditStatus === 'Draft') {
    return { color: 'amber', label: 'Status: School Drafting', priority: 2 };
  }

  return { color: 'amber', label: 'Status: Pending', priority: 2 };
}

/**
 * Registry "Action Required" buckets — same decision order as {@link getAuditTurn}
 * for `school` only (`audit` / `report` omitted).
 */
export function getRegistryActionBucket(school) {
  const auditStatus = school?.auditStatus || 'Draft';
  const workflowStep = school?.workflowStep || '';
  const refineryStep = school?.refineryStep || 0;
  const report = null;

  if (workflowStep === 'finalised') {
    return {
      order: 999,
      header: '0. INSTITUTIONAL MONITORING',
    };
  }

  if (school?.status === 'inactive') {
    return {
      order: 10,
      header: '1. NEW REGISTRATIONS: Activation Required',
    };
  }
  if (
    !isWorkflowPastEvidenceAnalysis(workflowStep) &&
    auditStatus === 'Submitted' &&
    (!report || refineryStep < 3)
  ) {
    return {
      order: 20,
      header: '2. REFINERY: AI Evidence Analysis Required',
    };
  }
  if (workflowStep === 'report_compiling' || workflowStep === 'ai_draft_ready') {
    return {
      order: 30,
      header: '3. AUTHORING: Report Drafting',
    };
  }
  if (workflowStep === 'peer_review') {
    const postAppeal = String(school?.qaPhase || '') === QA_PHASE_POST_APPEAL;
    return {
      order: 40,
      header: postAppeal
        ? '4. QUALITY ASSURANCE: Post-Appeal QA'
        : '4. QUALITY ASSURANCE: Internal Peer Review',
    };
  }
  if (workflowStep === 'internal_review_rejected') {
    return {
      order: 50,
      header: '5. AUTHORING: Peer Review Revisions',
    };
  }
  if (workflowStep === 'appeals') {
    return {
      order: 60,
      header: '6. SCHOOL FEEDBACK: Amend & Send to QA',
    };
  }
  if (workflowStep === 'accepted') {
    return {
      order: 70,
      header: '7. FINALISATION: Finalise & Issue Certificate',
    };
  }
  return {
    order: 999,
    header: '0. INSTITUTIONAL MONITORING',
  };
}

/**
 * Coarse Evidence Track lights: schema_v2 job when present; otherwise the
 * legacy `reports.meta.refinery` snapshot. A missing v2 job is not "not started"
 * when a completed legacy analysis already exists.
 */
export function resolveRegistryRefinery({
  pipelineIsSchemaV2,
  evidenceJob,
  reportRefinery,
  mapEvidenceJob,
  isAnalyzing = false,
}) {
  if (isAnalyzing) {
    return {
      evidence: { step: 0, label: 'resetting' },
      survey: { step: 0, label: 'resetting' },
    };
  }
  const legacy = reportRefinery && typeof reportRefinery === 'object'
    ? reportRefinery
    : EMPTY_REFINERY;
  if (pipelineIsSchemaV2 && evidenceJob && typeof mapEvidenceJob === 'function') {
    return {
      evidence: mapEvidenceJob(evidenceJob),
      survey: legacy.survey || EMPTY_REFINERY.survey,
    };
  }
  return {
    evidence: legacy.evidence || EMPTY_REFINERY.evidence,
    survey: legacy.survey || EMPTY_REFINERY.survey,
  };
}

/**
 * Open Report Editor: always allowed when a report already exists or the
 * cycle is past analysis. Creating a new shell still requires analysis ready.
 */
export function canOpenRegistryReportEditor({
  pipelineIsSchemaV2,
  evidenceJobReady,
  hasEvidenceJob,
  legacyRefineryStep,
  reportExists,
  workflowStep,
}) {
  if (reportExists || isWorkflowPastEvidenceAnalysis(workflowStep)) return true;
  if (pipelineIsSchemaV2 && hasEvidenceJob) return Boolean(evidenceJobReady);
  return Number(legacyRefineryStep || 0) >= 3;
}
